/**
 * M31 task-16 — the Configuration area of /admin/settings (HUB-092 §5).
 * Renders every registry key from GET /settings/config grouped by family;
 * each key saves on commit (PUT), custom keys reset to default (DELETE).
 * Direct HubClient calls via the singleton — the M27 IdentityProviders /
 * WebResearch precedent, NOT a data-provider case.
 *
 * Secrets: the stored value never reaches the browser (`hasValue` presence
 * marker only); the typed draft lives in local state and is cleared right
 * after a successful save.
 */
import { Alert } from '@/components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { hub } from '@/lib/hub-client';
import {
  groupSettings,
  descriptionFor,
  labelFor,
  rowRemountKey,
  valueKindFor,
  type HubSettingEntry,
} from '@/lib/settings-config';
import { SettingRow } from './SettingRow';
import { useSettingsEditor, type SettingsEditorApi } from './use-settings-editor';

/** Pinned per-key inline hints (HUB-092 §5). */
const KEY_HINTS: Record<string, string> = {
  'skills.seeds_enabled': 'applies on next hub restart',
};

/** Pinned per-family inline hints (HUB-092 §5). */
const GROUP_HINTS: Record<string, string> = {
  recall: 'changing recall knobs shifts eval baselines (COP-104) — measure before/after',
};

const HUB_SETTINGS_API: SettingsEditorApi<HubSettingEntry> = {
  list: async () => (await hub.settingsConfigList()).settings,
  put: (key, body) => hub.settingsConfigPut({ params: { key }, body }),
  remove: (key) => hub.settingsConfigDelete({ params: { key } }),
};

export function ConfigurationSection() {
  const { entries, loadError, saveError, encKeyError, fieldErrors, busyKey, save, reset } =
    useSettingsEditor(HUB_SETTINGS_API);

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold tracking-tight">Configuration</h2>
        <p className="text-sm text-muted-foreground">
          Every hub setting with its effective value. Custom values override the built-in
          defaults immediately.
        </p>
      </div>
      {encKeyError && <Alert variant="destructive">{encKeyError}</Alert>}
      {saveError && <Alert variant="destructive">{saveError}</Alert>}
      {loadError && <Alert variant="destructive">{loadError}</Alert>}
      {entries === null && !loadError && (
        <div className="text-sm text-muted-foreground">Loading…</div>
      )}
      {entries !== null &&
        groupSettings(entries).map((group) => (
          <Card key={group.family}>
            <CardHeader>
              <CardTitle className="text-base">{group.label}</CardTitle>
              {GROUP_HINTS[group.family] && (
                <CardDescription>{GROUP_HINTS[group.family]}</CardDescription>
              )}
            </CardHeader>
            <CardContent className="space-y-0 divide-y">
              {group.entries.map((entry) => (
                <SettingRow
                  // Value/source in the key remounts the row (and its draft)
                  // whenever a save or reset lands a new effective value.
                  key={rowRemountKey(entry)}
                  entry={entry}
                  kind={valueKindFor(entry.key, entry.value)}
                  label={labelFor(entry.key)}
                  description={descriptionFor(entry.key)}
                  hint={KEY_HINTS[entry.key]}
                  error={fieldErrors[entry.key]}
                  busy={busyKey === entry.key}
                  onSave={(kind, raw) => save(entry, kind, raw)}
                  onReset={() => void reset(entry.key)}
                />
              ))}
            </CardContent>
          </Card>
        ))}
    </section>
  );
}
